import { useI18n } from '@i18n';
import isNil from 'lodash/isNil';
import { useLocation } from 'react-router-dom';
import { BreadcrumbLink } from '@/components/molecules/Breadcrumbs/types';
import { useSession } from '@/components/session/useSession';
import { BreadcrumbNode, getBreadcrumbConfig } from '../breadcrumb-config';

const isNumericId = (segment: string) => /^\d+$/.test(segment);

/**
 * Resolves the next config node for a URL segment.
 *
 * Static segments are looked up by key. Numeric ID segments fall through to
 * the first child of the current node (the config uses a placeholder key of
 * `0` for dynamic ID segments, e.g. `scenarioId: 0`).
 *
 * Returns `undefined` when the segment has no match (unknown route or
 * numeric ID with no parent children configured).
 */
function resolveNode(
  segment: string,
  currentNode: BreadcrumbNode | undefined,
  config: Record<string, BreadcrumbNode>,
): BreadcrumbNode | undefined {
  const children = currentNode?.children ?? (isNil(currentNode) ? config : undefined);

  if (isNil(children)) return undefined;

  return children[segment] ?? (isNumericId(segment) ? Object.values(children).at(0) : undefined);
}

export function useBreadcrumbs(): BreadcrumbLink[] {
  const { pathname } = useLocation();
  const { t } = useI18n();
  const {
    org: { orgSector },
  } = useSession();

  const config = getBreadcrumbConfig(t, orgSector);
  const segments = pathname.split('/').filter(Boolean);

  const breadcrumbs: BreadcrumbLink[] = [];
  let currentNode: BreadcrumbNode | undefined;
  let currentPath = '';

  for (const segment of segments) {
    const node = resolveNode(segment, currentNode, config);
    if (isNil(node)) break;

    currentPath += `/${segment}`;
    const isCurrent = currentPath === pathname;
    // Numeric ID segments always use the runtime-built path as their href
    // because the config placeholder (id: 0) doesn't hold a real URL.
    const href = isCurrent ? undefined : isNumericId(segment) ? currentPath : node.href;

    breadcrumbs.push({ name: node.label, href, isCurrent });
    currentNode = node;
  }

  return breadcrumbs;
}
